import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/pages/Dashboard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, Download, CheckCircle2, AlertTriangle,
  XCircle, Loader2, RefreshCw,
} from "lucide-react";

/**
 * Excel Import - single-week migration flow for legacy spreadsheets.
 *
 * Step 1: drag-and-drop or pick a .xlsx → POST /import/roster/preview.
 * Step 2: review per-row preview, map unmatched employees, commit.
 *
 * Tokens are issued by the backend on preview and expire after 1 hour.
 * The commit endpoint deletes the staging row server-side so a token
 * cannot be replayed.
 */
export default function ImportRosterPage() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [empMappings, setEmpMappings] = useState({}); // excel_email -> employee_id | "" (skip)
  const [catMappings, setCatMappings] = useState({}); // excel_category -> category_id | "" (skip)
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);

  const loadHistory = () => {
    api.get("/import/history?limit=10").then(r => setHistory(r.data || [])).catch(()=>{});
  };

  useEffect(() => {
    if (user.role !== "company_admin") return;
    api.get("/employees").then(r => setEmployees(r.data || [])).catch(()=>{});
    api.get("/categories").then(r => setCategories((r.data || []).filter(c => c.is_active && c.kind === "work"))).catch(()=>{});
    loadHistory();
  }, [user.role]);

  if (user.role !== "company_admin") {
    return (
      <div>
        <h1 className="font-display text-4xl mb-3">Excel Import</h1>
        <p className="text-sm text-[#4B5773]">
          Only Workspace Admins can import rosters.
        </p>
      </div>
    );
  }

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "NextRota_Roster_Import_Template.xlsx"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Could not download template.");
    }
  };

  const submitFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Please upload a .xlsx file."); return;
    }
    setBusy(true); setPreview(null); setEmpMappings({}); setCatMappings({});
    try {
      const form = new FormData();
      form.append("file", f);
      const res = await fetch(`${API}/import/roster/preview`, {
        method: "POST", body: form, credentials: "include",
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(formatError(body.detail) || `Upload failed (${res.status}).`);
        return;
      }
      setPreview(body);
      setFile(f);
      // Seed default mappings ("" => skip).
      const emp = {}; body.summary.unmatched_employees.forEach(e => { emp[e] = ""; });
      setEmpMappings(emp);
      const cat = {}; (body.summary.unknown_categories || []).forEach(c => { cat[c] = ""; });
      setCatMappings(cat);
    } catch (e) {
      toast.error("Upload failed. Please try again.");
    } finally { setBusy(false); }
  };

  const reset = () => {
    setPreview(null); setFile(null); setEmpMappings({}); setCatMappings({});
    if (inputRef.current) inputRef.current.value = "";
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const employee_mappings = Object.entries(empMappings)
        .filter(([, eid]) => eid)
        .map(([excel_email, employee_id]) => ({ excel_email, employee_id }));
      const category_mappings = Object.entries(catMappings)
        .filter(([, cid]) => cid)
        .map(([excel_category, category_id]) => ({ excel_category, category_id }));
      const res = await api.post("/import/roster/commit", {
        import_token: preview.import_token, employee_mappings, category_mappings,
      });
      const out = res.data;
      toast.success(`Imported ${out.shifts_created} shift${out.shifts_created === 1 ? "" : "s"} and ${out.allocations_created} assignment${out.allocations_created === 1 ? "" : "s"} for the week of ${formatDate(out.target_week_start)}.`);
      reset();
      loadHistory();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Commit failed.");
    } finally { setCommitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <div className="eyebrow">Migration</div>
        <h1 className="font-display text-4xl mt-1">Import a roster from Excel</h1>
        <p className="text-sm text-[#4B5773] mt-2 max-w-2xl">
          Upload a filled-in NextRota template for a single Monday-Sunday week. We will preview every
          row before anything is written. The week lands as a draft - publish it from the Roster page when it is ready.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={downloadTemplate} data-testid="import-download-template-btn"
          variant="outline" className="gap-2">
          <Download className="h-4 w-4"/> Download template
        </Button>
        {preview && (
          <Button onClick={reset} variant="ghost" data-testid="import-reset-btn" className="gap-2">
            <RefreshCw className="h-4 w-4"/> Start over
          </Button>
        )}
      </div>

      {!preview && (
        <Card>
          <DropZone busy={busy} onPick={(f) => submitFile(f)} inputRef={inputRef} file={file}/>
        </Card>
      )}

      {preview && (
        <PreviewPanel
          preview={preview}
          employees={employees}
          categories={categories}
          empMappings={empMappings}
          setEmpMappings={setEmpMappings}
          catMappings={catMappings}
          setCatMappings={setCatMappings}
          onCommit={commit}
          committing={committing}
        />
      )}

      {!preview && history.length > 0 && <ImportHistory rows={history}/>}
    </div>
  );
}

function DropZone({ busy, onPick, inputRef, file }) {
  const [drag, setDrag] = useState(false);
  return (
    <label
      htmlFor="import-file-input"
      data-testid="import-dropzone"
      onDragOver={(e)=>{e.preventDefault(); setDrag(true);}}
      onDragLeave={()=>setDrag(false)}
      onDrop={(e)=>{e.preventDefault(); setDrag(false); onPick(e.dataTransfer.files?.[0]);}}
      className={`block cursor-pointer border-2 border-dashed rounded-xl p-10 text-center transition-colors ${drag ? "border-[#2563EB] bg-[#DBE7FE]" : "border-slate-300 hover:border-slate-400 bg-slate-50"}`}
    >
      <input
        ref={inputRef}
        id="import-file-input"
        data-testid="import-file-input"
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={(e)=>onPick(e.target.files?.[0])}
      />
      {busy ? (
        <div className="flex flex-col items-center text-[#4B5773]">
          <Loader2 className="h-8 w-8 animate-spin mb-3 text-[#2563EB]"/>
          <div className="font-medium">Parsing {file?.name || "file"}…</div>
          <div className="text-xs mt-1">Hang on while we read the workbook.</div>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <div className="h-12 w-12 rounded-full bg-[#DBE7FE] text-[#1D4ED8] grid place-items-center mb-3">
            <Upload className="h-5 w-5"/>
          </div>
          <div className="font-display text-lg">Drop your .xlsx here, or click to browse</div>
          <div className="text-xs text-[#4B5773] mt-1">
            Maximum 5 MB. Use the template above so the column headers match.
          </div>
        </div>
      )}
    </label>
  );
}

function PreviewPanel({ preview, employees, categories, empMappings, setEmpMappings,
                       catMappings, setCatMappings, onCommit, committing }) {
  const { summary, rows, target_week_start } = preview;
  const employeeOptions = useMemo(() => employees.map(e => ({
    value: e.employee_id, label: `${e.name} (${e.email})`,
  })), [employees]);
  const categoryOptions = useMemo(() => categories.map(c => ({
    value: c.category_id, label: c.name,
  })), [categories]);

  const hasErrors = summary.row_errors > 0;
  const unknownCats = summary.unknown_categories || [];

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="eyebrow">Preview</div>
            <div className="font-display text-2xl mt-1">
              Week of {formatDate(target_week_start)}
            </div>
            <div className="text-sm text-[#4B5773] mt-1 flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4"/>
              <span data-testid="import-summary-rows">{summary.total_rows} rows</span>
              <span>·</span>
              <span data-testid="import-summary-shifts">{summary.shifts_to_create} shift{summary.shifts_to_create === 1 ? "" : "s"} to create</span>
              <span>·</span>
              <span data-testid="import-summary-allocs">{summary.allocations_to_create} assignment{summary.allocations_to_create === 1 ? "" : "s"}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Pill tone="error" hidden={!summary.row_errors} testId="import-pill-errors">
              {summary.row_errors} error{summary.row_errors === 1 ? "" : "s"}
            </Pill>
            <Pill tone="warn" hidden={!summary.row_warnings} testId="import-pill-warnings">
              {summary.row_warnings} warning{summary.row_warnings === 1 ? "" : "s"}
            </Pill>
            <Pill tone="info" hidden={!summary.unknown_categories?.length} testId="import-pill-unkcat">
              {summary.unknown_categories.length} unknown categor{summary.unknown_categories.length === 1 ? "y" : "ies"}
            </Pill>
          </div>
        </div>

        {summary.unmatched_employees.length > 0 && (
          <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="import-mapping-block">
            <div className="font-medium text-amber-900 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4"/>
              Map {summary.unmatched_employees.length} unmatched email{summary.unmatched_employees.length === 1 ? "" : "s"}
            </div>
            <p className="text-xs text-amber-900/80 mt-1">
              We could not find these emails in your employee list. Map each one to an existing employee or
              leave it as Skip - skipped rows will not be imported.
            </p>
            <div className="mt-3 space-y-2">
              {summary.unmatched_employees.map(email => (
                <div key={email} className="flex flex-wrap items-center gap-3"
                     data-testid={`import-map-row-${email}`}>
                  <code className="text-xs bg-white border border-slate-200 rounded px-2 py-1">{email}</code>
                  <span className="text-xs text-[#4B5773]">maps to</span>
                  <Select
                    value={empMappings[email] || ""}
                    onValueChange={(v)=>setEmpMappings({...empMappings, [email]: v === "__skip" ? "" : v})}>
                    <SelectTrigger
                      data-testid={`import-map-select-${email}`}
                      className="h-9 w-72 rounded-md bg-white">
                      <SelectValue placeholder="Skip"/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__skip">Skip these rows</SelectItem>
                      {employeeOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        )}

        {unknownCats.length > 0 && (
          <div className="mt-5 rounded-lg border border-[#BFDBFE] bg-[#DBE7FE]/50 p-4"
               data-testid="import-cat-mapping-block">
            <div className="font-medium text-[#1D4ED8] flex items-center gap-2">
              <AlertTriangle className="h-4 w-4"/>
              Map {unknownCats.length} unknown categor{unknownCats.length === 1 ? "y" : "ies"}
            </div>
            <p className="text-xs text-[#1F2A44]/80 mt-1">
              These category names from your spreadsheet do not match any NextRota category. Map each one to
              an existing category or leave it as Skip - skipped rows will still import the shift, just without an assignment.
            </p>
            <div className="mt-3 space-y-2">
              {unknownCats.map(cat => (
                <div key={cat} className="flex flex-wrap items-center gap-3"
                     data-testid={`import-cat-map-row-${cat}`}>
                  <code className="text-xs bg-white border border-slate-200 rounded px-2 py-1">{cat}</code>
                  <span className="text-xs text-[#4B5773]">maps to</span>
                  <Select
                    value={catMappings[cat] || ""}
                    onValueChange={(v)=>setCatMappings({...catMappings, [cat]: v === "__skip" ? "" : v})}>
                    <SelectTrigger
                      data-testid={`import-cat-map-select-${cat}`}
                      className="h-9 w-72 rounded-md bg-white">
                      <SelectValue placeholder="Skip (shift only)"/>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__skip">Skip (shift only)</SelectItem>
                      {categoryOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card title="Rows">
        <div className="overflow-x-auto -mx-2">
          <table className="min-w-full text-sm" data-testid="import-rows-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[#4B5773] border-b border-slate-200">
                <th className="py-2 px-2">#</th>
                <th className="py-2 px-2">Employee</th>
                <th className="py-2 px-2">Date</th>
                <th className="py-2 px-2">Shift</th>
                <th className="py-2 px-2">Category</th>
                <th className="py-2 px-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.row_number} className="border-b border-slate-100"
                    data-testid={`import-row-${r.row_number}`}
                    data-row-status={r.errors.length ? "error" : (r.warnings.length ? "warn" : "ok")}>
                  <td className="py-2 px-2 text-[#4B5773]">{r.row_number}</td>
                  <td className="py-2 px-2">
                    {r.matched_employee_name ? (
                      <span>{r.matched_employee_name}</span>
                    ) : (
                      <code className="text-xs text-amber-700">{r.excel_email || "(blank)"}</code>
                    )}
                  </td>
                  <td className="py-2 px-2">{r.work_date || <span className="text-red-600">missing</span>}</td>
                  <td className="py-2 px-2">
                    {r.start_time && r.end_time ? (
                      <span>{r.start_time}-{r.end_time}{r.break_minutes ? ` · ${r.break_minutes}m break` : ""}</span>
                    ) : <span className="text-red-600">missing</span>}
                  </td>
                  <td className="py-2 px-2">
                    {r.matched_category_name
                      ? r.matched_category_name
                      : r.category_text
                        ? <span className="text-amber-700">{r.category_text} ?</span>
                        : <span className="text-[#4B5773]">-</span>}
                  </td>
                  <td className="py-2 px-2 align-top">
                    <RowStatus errors={r.errors} warnings={r.warnings}/>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={onCommit}
          disabled={committing || hasErrors}
          data-testid="import-commit-btn"
          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white gap-2">
          {committing ? <Loader2 className="h-4 w-4 animate-spin"/> : <CheckCircle2 className="h-4 w-4"/>}
          {committing ? "Committing…" : "Commit import"}
        </Button>
        {hasErrors && (
          <div className="text-xs text-red-700 flex items-center gap-1.5">
            <XCircle className="h-3.5 w-3.5"/> Fix the {summary.row_errors} row error{summary.row_errors === 1 ? "" : "s"} before committing.
          </div>
        )}
        <Link to="/app/roster" data-testid="import-cancel-link"
          className="text-xs text-[#4B5773] hover:text-[#0B1A36] underline">Cancel and return to roster</Link>
      </div>
    </div>
  );
}

function RowStatus({ errors, warnings }) {
  if (errors.length) return (
    <div className="text-xs text-red-700">
      {errors.map((e, i) => <div key={i} className="flex items-start gap-1.5"><XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0"/>{e}</div>)}
    </div>
  );
  if (warnings.length) return (
    <div className="text-xs text-amber-800">
      {warnings.map((w, i) => <div key={i} className="flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0"/>{w}</div>)}
    </div>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
      <CheckCircle2 className="h-3.5 w-3.5"/> Ready
    </span>
  );
}

function Pill({ tone, hidden, testId, children }) {
  if (hidden) return null;
  const cls = tone === "error"
    ? "bg-red-50 text-red-700 border-red-200"
    : tone === "warn"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : "bg-[#DBE7FE] text-[#1D4ED8] border-[#BFDBFE]";
  return (
    <span data-testid={testId}
      className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border ${cls}`}>
      {children}
    </span>
  );
}

function ImportHistory({ rows }) {
  return (
    <div data-testid="import-history">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="eyebrow">Past imports</div>
          <h2 className="font-display text-2xl mt-1">Import history</h2>
          <p className="text-xs text-[#4B5773] mt-1">
            Your last {rows.length} {rows.length === 1 ? "import" : "imports"}. Use this to trace whether a roster came from Excel or was built in NextRota.
          </p>
        </div>
      </div>
      <Card>
        <div className="overflow-x-auto -mx-2">
          <table className="min-w-full text-sm" data-testid="import-history-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[#4B5773] border-b border-slate-200">
                <th className="py-2 px-2">When</th>
                <th className="py-2 px-2">By</th>
                <th className="py-2 px-2">Target week</th>
                <th className="py-2 px-2 text-right">Rows</th>
                <th className="py-2 px-2 text-right">Shifts</th>
                <th className="py-2 px-2 text-right">Assignments</th>
                <th className="py-2 px-2 text-right">Skipped</th>
                <th className="py-2 px-2">Status</th>
                <th className="py-2 px-2">File</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((h, i) => {
                const skipParts = [];
                const sk = h.skipped || {};
                if (sk.unmapped) skipParts.push(`${sk.unmapped} unmapped`);
                if (sk.errors) skipParts.push(`${sk.errors} error${sk.errors === 1 ? "" : "s"}`);
                if (sk.leave_collision) skipParts.push(`${sk.leave_collision} on leave`);
                const skipTitle = skipParts.join(", ");
                return (
                  <tr key={h.log_id || i}
                      className="border-b border-slate-100"
                      data-testid={`import-history-row-${i}`}
                      data-import-status={h.status}>
                    <td className="py-2 px-2 whitespace-nowrap" title={h.timestamp}>{formatRelativeOrDate(h.timestamp)}</td>
                    <td className="py-2 px-2 whitespace-nowrap">{h.user_name || h.user_email}</td>
                    <td className="py-2 px-2 whitespace-nowrap">{formatDate(h.target_week_start)}</td>
                    <td className="py-2 px-2 text-right">{h.rows_total ?? "-"}</td>
                    <td className="py-2 px-2 text-right">{h.shifts_created}</td>
                    <td className="py-2 px-2 text-right">{h.allocations_created}</td>
                    <td className="py-2 px-2 text-right" title={skipTitle}>{h.skipped_total || 0}</td>
                    <td className="py-2 px-2"><StatusPill status={h.status}/></td>
                    <td className="py-2 px-2 truncate max-w-[160px]" title={h.filename || ""}>
                      {h.filename || <span className="text-[#4B5773]">-</span>}
                    </td>
                    <td className="py-2 px-2">
                      <Link
                        to={`/app/roster?week_start=${h.target_week_start}`}
                        data-testid={`import-history-view-${i}`}
                        className="text-xs font-medium text-[#2563EB] hover:underline whitespace-nowrap"
                      >
                        View week →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function StatusPill({ status }) {
  const cls = status === "completed"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status === "partial"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : "bg-red-50 text-red-700 border-red-200";
  const label = status === "completed" ? "Completed"
    : status === "partial" ? "Partial"
    : status === "failed" ? "Failed"
    : (status || "-");
  return (
    <span data-testid={`status-pill-${status}`}
      className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border ${cls}`}>
      {label}
    </span>
  );
}

function formatRelativeOrDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} d ago`;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}
